import { createTheme } from '@mui/material/styles';
import { colors } from './design-tokens';

// NOTE THEME IS INDEXED FOR STORYBOOK!
// CHANGES UPDATE IN STORYBOOK TO AVOID MISMATCHED STYLES

/** NEW ITEMS SHOULD BE ADDED TO THE BOTTOM OF THE TOP-LEVEL PROPERTY
 * e.g.
 * components: {
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: 0,
        },
      },
    },
    ... <-- ADD NEW COMPONENT OVERRIDE HERE
 * palette: {
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.54)',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    ... <-- ADD NEW PALETTE STYLE HERE
 */
export const defaultTheme = {
  components: {
    MuiFormLabel: {
      styleOverrides: {
        asterisk: { color: colors.error.main },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: 0,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': {
            borderBottom: 0,
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: 16,
          fontSize: '1.25rem',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minWidth: 'max-content',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#4baef8',
          '&:hover': {
            color: '#2991E9',
          },
          '&.MuiLink-underlineAlways': {
            textDecoration: 'none',
          },
        },
      },
    },
  },
  typography: {
    button: {
      fontWeight: 700,
    },
    h1: {
      fontSize: 36,
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: 'normal',
    },
    h2: {
      fontSize: 24,
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: 'normal',
    },
    h3: {
      fontSize: 21,
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: 'normal',
    },
    h4: {
      fontSize: 18,
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: 'normal',
    },
    h5: {
      fontSize: 17,
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: 'normal',
    },
    h6: {
      fontSize: 16,
      fontWeight: 700,
      lineHeight: 1.6,
      letterSpacing: 'normal',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '.875rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    caption: {
      display: 'block',
      fontSize: '.75rem',
      fontWeight: 400,
      letterSpacing: 'normal',
    },
    overline: {
      display: 'block',
      fontSize: '.75rem',
      fontWeight: 700,
      lineHeight: 'inherit',
      textTransform: 'uppercase' as any,
    },
    fontFamily: [
      'Lato',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
  },
  palette: {
    common: colors.common,
    gray: colors.gray,
    background: colors.background,
    primary: colors.primary,
    secondary: colors.secondary,
    tertiary: colors.tertiary,
    success: colors.success,
    caution: colors.caution,
    alert: colors.alert,
    error: colors.error,
    critical: colors.critical,
    info: colors.info,
    neutral: colors.neutral,
    fulcrum: colors.fulcrum,
    text: colors.text,
  },
};

const nonStandardColors = new Set(['text', 'common', 'background']);
export const isButtonColor = (color: string) => !nonStandardColors.has(color);

const theme = createTheme(defaultTheme);

export default theme;
